import type { InfiniteData, QueryClient, QueryKey } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import {
  patchListedChannel,
  removeListedChannel,
} from '~hooks/useMessaging/messagingChannelsCache';
import { MessagingChannel } from '~hooks/useMessaging/models';
import {
  isListBeingRead,
  reReadPatchedList,
  writePatchedList,
} from '~hooks/useMessaging/reReadPatchedList';
import {
  isChannelFilteredOutOfList,
  MESSAGING_CHANNEL_QUERY_KEY,
  MESSAGING_CHANNELS_QUERY_KEY,
  MESSAGING_TARGET_CHANNEL_QUERY_KEY,
} from '~queries/messagingQueryKeys';
import type { MessagingChannelsPage } from '~queries/useMessagingChannelsInfiniteQuery';
import { Nullable } from '~types/Nullable';

const patchVersionsByClient = new WeakMap<QueryClient, Map<string, number>>();

/** Moves whenever a channel's cached read state is patched, so a caller can tell one landed meanwhile. */
export const channelPatchVersion = (queryClient: QueryClient, channelId: string) =>
  patchVersionsByClient.get(queryClient)?.get(channelId) ?? 0;

function bumpChannelPatchVersion(queryClient: QueryClient, channelId: string) {
  const patchVersions =
    patchVersionsByClient.get(queryClient) ?? new Map<string, number>();
  patchVersionsByClient.set(queryClient, patchVersions);
  patchVersions.set(channelId, (patchVersions.get(channelId) ?? 0) + 1);
}

export default function useMessagingChannelCachePatch() {
  const queryClient = useQueryClient();

  const patchChannelInCaches = useCallback(
    (
      channelId: string,
      patch: (channel: MessagingChannel) => MessagingChannel,
      { shouldReReadListsLacking = false }: { shouldReReadListsLacking?: boolean } = {},
    ) => {
      bumpChannelPatchVersion(queryClient, channelId);

      const channelLists = queryClient.getQueriesData<
        InfiniteData<MessagingChannelsPage>
      >({ queryKey: MESSAGING_CHANNELS_QUERY_KEY });
      const listsLackingChannel: QueryKey[] = [];
      let lastPatchedChannel: MessagingChannel | undefined;

      for (const [channelListKey, channelList] of channelLists) {
        let patchedChannel: MessagingChannel | undefined;
        const patchedList =
          channelList &&
          patchListedChannel(channelList, channelId, (channel) => {
            patchedChannel = patch(channel);

            return patchedChannel;
          });
        if (!patchedList) {
          listsLackingChannel.push(channelListKey);
          continue;
        }

        lastPatchedChannel = patchedChannel;

        const isPatchedChannelFilteredOut =
          patchedChannel !== undefined &&
          isChannelFilteredOutOfList(channelListKey, patchedChannel);

        writePatchedList(
          queryClient,
          channelListKey,
          isPatchedChannelFilteredOut
            ? removeListedChannel(patchedList, channelId)
            : patchedList,
        );
      }

      if (shouldReReadListsLacking && lastPatchedChannel) {
        for (const channelListKey of listsLackingChannel) {
          if (isChannelFilteredOutOfList(channelListKey, lastPatchedChannel)) continue;

          if (isListBeingRead(queryClient, channelListKey)) {
            void reReadPatchedList(queryClient, channelListKey);
          } else {
            void queryClient.invalidateQueries({ queryKey: channelListKey, exact: true });
          }
        }
      }

      for (const queryKey of [
        MESSAGING_CHANNEL_QUERY_KEY,
        MESSAGING_TARGET_CHANNEL_QUERY_KEY,
      ]) {
        const cachedChannels = queryClient.getQueriesData<Nullable<MessagingChannel>>({
          queryKey,
        });
        for (const [cachedKey, cachedChannel] of cachedChannels) {
          if (cachedChannel?.id !== channelId) continue;

          writePatchedList(queryClient, cachedKey, patch(cachedChannel));
        }
      }
    },
    [queryClient],
  );

  return { patchChannelInCaches };
}
